/** "Run now": what the run will cover and what it will cost, BEFORE the
 *  click. Three plain choices (new conversations since the last run,
 *  re-analyze a date range, re-analyze everything), a live estimate from the
 *  engine's own selection and plan (GET /retroactive-runs/estimate), this
 *  week's manual runs, and every refusal in the person's language. One run
 *  covers the newest `estimate.limit` conversations it selects, and says so
 *  when there are more: a range that ends earlier reaches older ones. Run
 *  starts a row and the dialog closes: progress shows in the template's row
 *  (the tab's polling also works the run), and leaving the page is fine. */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { DatePicker, Modal, Spin } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { PlayCircleOutlined } from '@ant-design/icons';
import { useLanguage } from '@/hooks/useLanguage';
import { datePattern } from '@/utils/locale';
import { formatRelativeTime } from '@/utils/relativeTime';
import insightsService from '../../services/insightsService';
import type { InsightTemplate, InsightsQuota, RetroactiveRun, RunEstimate, RunMode } from '../../types/insights';
import { errorMessage, formatCount, formatEstimate, formatUsd } from './templateParts';

interface Props {
  open: boolean;
  template: InsightTemplate | null;
  quota: InsightsQuota | null;
  onClose: () => void;
  onStarted: (run: RetroactiveRun) => void;
}

const ESTIMATE_DELAY_MS = 300;

export function RunTemplateDialog({ open, template, quota, onClose, onStarted }: Props) {
  const { t } = useLanguage();
  const [mode, setMode] = useState<RunMode>('new');
  const [dateFrom, setDateFrom] = useState<Dayjs>(() => dayjs().subtract(30, 'day'));
  const [dateTo, setDateTo] = useState<Dayjs>(() => dayjs());
  const [estimate, setEstimate] = useState<RunEstimate | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [estimateError, setEstimateError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setMode('new');
    setDateFrom(dayjs().subtract(30, 'day'));
    setDateTo(dayjs());
    setStartError(null);
  }, [open, template?.template_id]);

  const dateValue = dateFrom.format('YYYY-MM-DD');
  const dateToValue = dateTo.format('YYYY-MM-DD');
  const range = mode === 'from_date' ? { date_from: dateValue, date_to: dateToValue } : {};
  useEffect(() => {
    if (!open || !template) return;
    let cancelled = false;
    setEstimating(true);
    setEstimateError(null);
    const timer = window.setTimeout(() => {
      insightsService.estimateTemplateRun({ template_id: template.template_id, mode, ...range })
        .then((e) => { if (!cancelled) setEstimate(e); })
        .catch((err) => { if (!cancelled) { setEstimate(null); setEstimateError(errorMessage(t, err)); } })
        .finally(() => { if (!cancelled) setEstimating(false); });
    }, ESTIMATE_DELAY_MS);
    return () => { cancelled = true; window.clearTimeout(timer); };
    // `range` stands for mode + the two dates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, template, mode, dateValue, dateToValue, t]);

  const manual = quota?.manual_runs;
  const exempt = !!quota?.is_exempt || (manual?.limit ?? 0) < 0;
  const quotaReached = !exempt && !!manual && manual.remaining <= 0;
  const lookbackDays = exempt ? null : quota?.lookback_days ?? null;

  const blocker = useMemo((): string | null => {
    if (quotaReached && manual) {
      return manual.resets_at
        ? t('insights.tpl.run.quotaReachedUntil', {
          limit: formatCount(manual.limit), when: dayjs(manual.resets_at).format(datePattern('monthDay')),
        })
        : t('insights.tpl.run.quotaReached', { limit: formatCount(manual.limit) });
    }
    if (!estimate) return null;
    if (estimate.spend?.reached) {
      return t('insights.tpl.errors.SPEND_LIMIT', { budget: formatUsd(estimate.spend.budget_usd) });
    }
    if (estimate.to_analyze === 0) {
      return mode === 'new' ? t('insights.tpl.run.nothingNew') : t('insights.tpl.run.nothingInRange');
    }
    return null;
  }, [quotaReached, manual, estimate, mode, t]);

  const start = async () => {
    if (!template) return;
    setStarting(true);
    setStartError(null);
    try {
      const run = await insightsService.startTemplateRun({ template_id: template.template_id, mode, ...range });
      onStarted(run);
    } catch (err) {
      setStartError(errorMessage(t, err));
    } finally {
      setStarting(false);
    }
  };

  const reads = (e: RunEstimate) => {
    if (e.reads.transcript && e.reads.summary) return t('insights.tpl.run.readsBoth');
    return e.reads.transcript ? t('insights.tpl.run.readsFull') : t('insights.tpl.run.readsSummaries');
  };

  const choices: { value: RunMode; heading: string; desc: string }[] = [
    {
      value: 'new',
      heading: t('insights.tpl.run.optNew'),
      desc: template?.last_run_at
        ? t('insights.tpl.run.optNewDesc', { when: formatRelativeTime(template.last_run_at) })
        : t('insights.tpl.run.optNewNever'),
    },
    {
      value: 'from_date',
      heading: t('insights.tpl.run.optFrom'),
      desc: t('insights.tpl.run.optFromDesc', {
        from: dateFrom.format(datePattern('monthDayYear')), to: dateTo.format(datePattern('monthDayYear')),
      }),
    },
    {
      value: 'everything',
      heading: t('insights.tpl.run.optAll'),
      desc: template?.total_results_count
        ? t('insights.tpl.run.optAllDesc', {
          count: template.total_results_count, n: formatCount(template.total_results_count),
        })
        : t('insights.tpl.run.optAllNone'),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={520}
      destroyOnHidden
      title={t('insights.tpl.run.title', { name: template?.template_name ?? '' })}
      footer={(
        <div className="tpl-dialog__footer">
          <button type="button" className="cx-btn" onClick={onClose}>{t('common.cancel')}</button>
          <button type="button" className="cx-btn cx-btn--primary" onClick={start}
                  disabled={!template || starting || estimating || !!blocker || !!estimateError}
                  data-testid="run-template-confirm">
            <PlayCircleOutlined aria-hidden />
            {t('insights.tpl.run.run')}
          </button>
        </div>
      )}
      data-testid="run-template-dialog"
    >
      <div className="tpl-choices" role="radiogroup" aria-label={t('insights.tpl.run.whatToAnalyze')}>
        {choices.map((c) => (
          <Fragment key={c.value}>
            <label className={`tpl-choice${mode === c.value ? ' is-selected' : ''}`}>
              <input type="radio" name="tpl-run-mode" value={c.value} checked={mode === c.value}
                     onChange={() => setMode(c.value)} />
              <span className="tpl-choice__text">
                <span className="tpl-choice__title">{c.heading}</span>
                <span className="tpl-choice__desc">{c.desc}</span>
              </span>
            </label>
            {c.value === 'from_date' && mode === 'from_date' && (
              <div className="tpl-choice__extra">
                <DatePicker.RangePicker
                  value={[dateFrom, dateTo]}
                  onChange={(dates) => {
                    if (dates?.[0] && dates[1]) { setDateFrom(dates[0]); setDateTo(dates[1]); }
                  }}
                  allowClear={false}
                  format={datePattern('monthDayYear')}
                  aria-label={t('insights.tpl.run.pickRange')}
                  disabledDate={(d) => d.isAfter(dayjs(), 'day')
                    || (lookbackDays !== null && d.isBefore(dayjs().subtract(lookbackDays, 'day'), 'day'))}
                />
              </div>
            )}
          </Fragment>
        ))}
      </div>

      <div className="tpl-estimate" aria-live="polite">
        <span className="tpl-estimate__label">{t('insights.tpl.run.estimate')}</span>
        {estimating && !estimate ? <Spin size="small" /> : estimate ? (
          <>
            <p className="tpl-estimate__line">
              {t('insights.tpl.run.estimateLine', {
                n: formatCount(estimate.to_analyze), model: estimate.model_name, reads: reads(estimate),
                cost: formatEstimate(t, estimate.estimated_cost_usd),
              })}
            </p>
            {estimate.to_analyze > 0 && (
              <p className="tpl-estimate__more" data-testid="run-per-1000">
                {t('insights.tpl.run.per1000', {
                  cost: formatEstimate(t, estimate.cost_per_1000_usd), model: estimate.model_name,
                })}
              </p>
            )}
            {estimate.conversation_count > estimate.covered && (
              <p className="tpl-estimate__more" data-testid="run-newest-only">
                {t('insights.tpl.run.newestOnly', {
                  limit: formatCount(estimate.limit), total: formatCount(estimate.conversation_count),
                })}
              </p>
            )}
            {mode === 'new' && estimate.unchanged > 0 && (
              <p className="tpl-estimate__more">
                {t('insights.tpl.run.unchanged', { count: estimate.unchanged, n: formatCount(estimate.unchanged) })}
              </p>
            )}
            {estimate.skipped > 0 && (
              <p className="tpl-estimate__more">
                {t('insights.tpl.run.skipped', { count: estimate.skipped, n: formatCount(estimate.skipped) })}
              </p>
            )}
          </>
        ) : estimateError ? <p className="tpl-error" role="alert">{estimateError}</p> : null}
      </div>

      {manual && !exempt && (
        <p className="tpl-muted tpl-run__quota">
          {t('insights.tpl.run.quotaLine', { used: formatCount(manual.used), limit: formatCount(manual.limit) })}
        </p>
      )}
      {(blocker || startError) && <p className="tpl-error" role="alert">{startError || blocker}</p>}
      <p className="tpl-muted tpl-run__leave">{t('insights.tpl.run.leaveOk')}</p>
    </Modal>
  );
}
